import { CardProductDto, TravelCardDto, TravelCardOrderDetailsDto, TravelCardTopUpDataDto } from '@/types/webapi';
import { isWithinHoursFromNow } from '@/helpers/TimestampFilters';
import { PURCHASE_NOTIFICATION_VISIBILITY_VALUE } from '@/constants';
import { differenceInHours, fromUnixTime } from 'date-fns';

/**
 * Sorts an array of objects by createdAt date in ascending order (oldest first)
 * @param response Array of objects containing createdAt date property
 * @returns Sorted array with oldest items first
 */
export const sortByDate = <T extends { createdAt: Date }>(response: T[]) =>
    response?.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());

/**
 * Returns Joined string with Card numbers
 * @param items Array of items
 * @returns Joined string with Card numbers
 */
export const getCardNumberList = (items: TravelCardDto[] = []): string => items?.map(card => card.number).join(',');

/**
 * Merges travel card data with travel order data
 * @param travelCards Array of travel cards
 * @param travelCardOrders Array of travel order data
 * @returns Array of merged travel card objects with order data
 */
export const mergeTravelCardData = (
    travelCards: TravelCardDto[],
    travelCardOrders: TravelCardOrderDetailsDto[] | undefined
): TravelCardDto[] => {
    if (!travelCardOrders?.length) return travelCards;
    return travelCards.map(travelCard => {
        const travelCardOrderData = travelCardOrders?.find(travelOrder => travelOrder.id === travelCard.number);
        const pendingTravelCardTopUps = getPendingTravelCardTopUp(travelCardOrderData?.pendingProducts);
        const travelCardTicketOrder = travelCardOrderData?.pendingProducts?.filter(travelCardOrder => !travelCardOrder.isTravelMoney) || [];

        return {
            ...travelCard,
            pendingProducts: travelCardTicketOrder || [],
            products: travelCardOrderData?.products || [],
            travelMoneyAmount: getTravelMoneyAmount(travelCardOrderData),
            autoTravelProduct: travelCardOrderData?.autoTravelProduct || [],
            pendingTravelMoney: pendingTravelCardTopUps || []
        } as TravelCardDto;
    });
};

/**
 * Extracts card numbers from an array of travel cards
 * @param cards Array of travel card objects
 * @returns Array of card numbers
 */
export const getTravelCardNumberList = (cards: TravelCardDto[] = []): string[] => cards.map(card => card.number);

/**
 * Returns the first N items from an array
 * @param items Array of items to slice
 * @param amount Number of items to return from start of array
 * @returns Sliced array containing first N items
 */
export const getFirst = <T>(items: T[], amount?: number): T[] => items?.slice(0, amount) || [];

/**
 * Sorts travel cards into new and old types based on card number length
 * @param items Array of items containing card numbers
 * @param take
 * @param cardType
 * @returns Object with arrays of new (9-digit) and old (16-digit) cards
 */
export const getCardsByCardType = (items: TravelCardDto[] = [], cardType: number, take?: number): TravelCardDto[] => {
    if (!items?.length) return [];

    return getFirst(
        items.filter(item => item.number.length === cardType),
        take
    );
};

/**
 * Retrieves pending travel card top-ups within a defined time limit
 *
 * @param travelCardOrder Array of card products to evaluate
 * @returns Array of travel card top-up data with amount and creation time
 *
 * Filters travel card products that are marked as travel money and were ordered
 * within a specified number of hours (`PRODUCT_TIME_DEFAULT_LIMIT`).
 * Returns a simplified structure containing only the amount and order time.
 */
export const getPendingTravelCardTopUp = (travelCardOrder: CardProductDto[] | undefined): TravelCardTopUpDataDto[] =>
    travelCardOrder
        ?.filter(
            pendingProduct =>
                pendingProduct.isTravelMoney && isWithinHoursFromNow(pendingProduct.orderTime, PURCHASE_NOTIFICATION_VISIBILITY_VALUE)
        )
        .map(pendingProduct => ({
            travelMoneyAmount: pendingProduct.amount!,
            createdAt: fromUnixTime(pendingProduct.orderTime)
        })) ?? ([] as TravelCardTopUpDataDto[]);

/**
 * Calculates the total travel money amount from purchased products and pending top-ups that have passed the time limit.
 * @param travelCardOrderDetails - Travel card order details object
 * @returns Total travel money amount
 */
export const getTravelMoneyAmount = (travelCardOrderDetails?: TravelCardOrderDetailsDto, now?: Date): number => {
    const purchasedTravelMoney = travelCardOrderDetails?.travelMoneyAmount || 0;
    now ||= new Date();

    const pendingTravelMoney =
        travelCardOrderDetails?.pendingProducts
            .filter(
                pendingProduct =>
                    pendingProduct.isTravelMoney &&
                    differenceInHours(now, fromUnixTime(pendingProduct.orderTime)) > PURCHASE_NOTIFICATION_VISIBILITY_VALUE
            )
            .reduce((total, product) => total + (product.amount || 0), 0) || 0;

    return purchasedTravelMoney + pendingTravelMoney;
};
